import { useMemo } from 'react';
import { useGetQuestionnaireGroupList } from '@viome/viome-base/src/usecase/Questionnaire/Questionnaire.query';
import { Skeleton } from '@design-system/Skeleton';
import { GreenTickMarkIcon } from '@/components/icons/GreenTickIcon';
import { CircularProgressIndicator } from '@design-system/CircularProgressIndicator';
import { NavBar } from '@design-system/NavBar';
import { useNavigate } from 'react-router-dom';
import SuccessGreenLottie from '@/assets/success-green.json';
import { Player } from '@lottiefiles/react-lottie-player';

const toTitleCase = (value?: string) => {
  if (!value) return '';
  const spaced = value.replace(/[_-]+/g, ' ').trim();
  return spaced
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const QuestionnaireLandingPage = () => {
  const navigate = useNavigate();

  const {
    data: questionnaireGroupData,
    isLoading: isQuestionnaireGroupLoading,
    isError: isQuestionnaireGroupError,
  } = useGetQuestionnaireGroupList();

  const questionnairesData = useMemo(() => {
    if (!questionnaireGroupData?.question_group) {
      return [];
    }

    return questionnaireGroupData.question_group
      .sort((a, b) => a.ordinal - b.ordinal)
      .map((group) => ({
        image: group.image_url,
        title: toTitleCase(group.name),
        description: group.description,
        timing:
          group.minutes_to_complete === 0
            ? '0 mins'
            : `${group.minutes_to_complete} mins`,
        progress:
          group.no_of_questions > 0
            ? Math.round(
                (group.no_of_answered_questions / group.no_of_questions) * 100,
              )
            : 0,
        isCompleted:
          group.no_of_questions > 0 &&
          group.no_of_answered_questions >= group.no_of_questions,
        groupId: group.questioniare_group_id,
        name: group.name,
        enable_read_only_mode:
          questionnaireGroupData?.enable_read_only_mode || false,
      }));
  }, [questionnaireGroupData]);

  const overallProgress = useMemo(() => {
    if (!questionnaireGroupData) return 0;

    const totalQuestions = questionnaireGroupData.no_of_questions;
    const answeredQuestions = questionnaireGroupData.no_of_answered_questions;

    return totalQuestions > 0
      ? Math.round((answeredQuestions / totalQuestions) * 100)
      : 0;
  }, [questionnaireGroupData]);

  return (
    <section className="w-full page-container">
      <NavBar
        onReturnClick={() => navigate('/')}
        returnButtonTitle="Home"
        className="md:-ml-5 mt-8"
      />
      {overallProgress >= 100 ? (
        <div className="flex flex-col items-center justify-center text-center mt-16 mb-12">
          <Player
            autoplay
            src={SuccessGreenLottie}
            keepLastFrame
            className="w-16 h-16 object-cover"
            data-testid="questionnaireSuccess-animation"
          />
          <p className="typography-h1 text-primary mb-4">
            Questionnaire Completed
          </p>
          <p className="typography-body text-secondary max-w-[600px]">
            Combine with your samples, we use your answers to inform your results and recommendations.
          </p>
        </div>
      ) : (
        <>
          <p className="typography-d2 text-primary mt-8 mb-6 !font-normal">Questionnaire</p>
          <p className="typography-body text-secondary mb-12">
            Your test results shape your personalized plan, but your input adds
            valuable context—like your current health, goals, and habits—to make
            your recommendations not just effective, but also safe, practical,
            and tailored to your lifestyle.
          </p>
        </>
      )}

      {!isQuestionnaireGroupLoading && !isQuestionnaireGroupError && (
        <div className="mb-6">
          <div className="flex justify-between mb-2">
            <span className="typography-body text-secondary">
              Total progress
            </span>
            <span className="typography-body text-secondary">
              {overallProgress}%
            </span>
          </div>
          <div className="w-full h-1 bg-background-secondary rounded-full overflow-hidden">
            <div
              className="h-full bg-background-success transition-all duration-300 ease-in-out"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {isQuestionnaireGroupLoading ? (
          Array.from({ length: 8 }).map((_, index) => (
            <Skeleton
              key={index}
              className="h-[100px] w-full min-w-[330px] border border-gray-300"
            />
          ))
        ) : isQuestionnaireGroupError ? (
          <div className="col-span-1 lg:col-span-2 text-center py-8">
            <p className="text-red-500">
              Failed to load questionnaires. Please try again.
            </p>
          </div>
        ) : (
          questionnairesData.map((questionnaire) => (
            <div
              key={questionnaire.groupId}
              className="bg-background-blur-subtle rounded-2xl p-4 cursor-pointer hover:bg-background-blur-medium transition-colors relative backdrop-blur-[25px] shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_0_0_rgba(255,255,255,0.04)_inset]"
              onClick={() => {
                if (questionnaire.progress > 0 && questionnaire.progress >= 100) {
                  // If user has started answering, go directly to questions
                  navigate(
                    `/questionnaires/${questionnaire.groupId}/questions`,
                    {
                      state: { questionnaire },
                    },
                  );
                } else {
                  // If user hasn't started, show details page
                  navigate(`/questionnaires/${questionnaire.groupId}`, {
                    state: { questionnaire },
                  });
                }
              }}
            >
              <div className="flex items-start gap-4">
                <img
                  src={questionnaire.image}
                  alt={questionnaire.title}
                  className="w-16 h-16 shrink-0 rounded-lg object-cover"
                  onError={(e) => {
                    e.currentTarget.src =
                      'https://res.cloudinary.com/djog4qbdw/image/upload/v1750157156/Rectangle_52431304_id91qr.png';
                  }}
                />
                <div className="flex-1 flex flex-col h-16 justify-between">
                  <h3 className="typography-title text-lg font-medium line-clamp-1">
                    {questionnaire.title}
                  </h3>
                  <p className="text-tertiary text-sm">
                    {questionnaire.timing}
                  </p>
                </div>
                <div className="shrink-0">
                  {questionnaire.isCompleted ? (
                    <GreenTickMarkIcon className="w-6 h-6" />
                  ) : (
                    <CircularProgressIndicator
                      progress={questionnaire.progress}
                      size={24}
                      strokeWidth={2.5}
                    />
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
};
